// src/db.js
import { firestoreDb, storage } from './firebase';
import { collection, getDocs, limit, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, getDoc, onSnapshot, setDoc, arrayUnion, arrayRemove, getCountFromServer } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  subscribeToActiveBoss, 
  subscribeToTopHunters, 
  subscribeToUserParticipation, 
  recordWorkoutBossDamage, 
  recordTapDamage, 
  queueTapDamage,
  claimBossReward, 
  resetBossHp, 
  calculateWorkoutTonnage 
} from './services/bossService';

// -- USERS / ONBOARDING --
export const saveUserOnboarding = async (uid, data) => {
  await setDoc(doc(firestoreDb, "users", uid), {
    ...data,
    createdAt: new Date().toISOString()
  });
};

// -- PROFILES --
export const getProfiles = async (userId = null) => {
  // If we only want profiles for the logged-in user:
  let q = collection(firestoreDb, "profiles");
  if (userId) {
    q = query(q, where("userIds", "array-contains", userId));
  }
  
  const querySnapshot = await getDocs(q);
  const profiles = [];
  querySnapshot.forEach((doc) => {
    profiles.push({ ...doc.data(), id: doc.id });
  });
  return profiles;
};

// Pour l'écran de récupération
export const getUnclaimedProfiles = async () => {
  const querySnapshot = await getDocs(collection(firestoreDb, "profiles"));
  const profiles = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    if (!data.userIds || data.userIds.length === 0) {
      profiles.push({ ...data, id: doc.id });
    }
  });
  return profiles;
};

export const claimProfiles = async (profileIds, userId) => {
  const promises = profileIds.map(async (profileId) => {
    const profileRef = doc(firestoreDb, "profiles", profileId);
    const snap = await getDoc(profileRef);
    if (snap.exists()) {
      const data = snap.data();
      const userIds = data.userIds || [];
      if (!userIds.includes(userId)) {
        await updateDoc(profileRef, { userIds: [...userIds, userId] });
      }
    }
  });
  await Promise.all(promises);
};

export const addProfile = async (profile, userId) => {
  let isBeta = false;
  let bonusCoins = 0;

  try {
    const [betaSnap, totalProfilesSnap] = await Promise.all([
      getCountFromServer(query(collection(firestoreDb, "profiles"), where("isBetaTester", "==", true))),
      getCountFromServer(collection(firestoreDb, "profiles"))
    ]);

    const currentBetaCount = betaSnap.data().count;
    const currentTotalProfiles = totalProfilesSnap.data().count;

    // 11 utilisateurs existants + 30 prochains = 41 places pour le badge Bêta Testeur
    if (currentBetaCount < 41) {
      isBeta = true;
    }

    // 11 utilisateurs existants + 20 prochains = 31 profils max qui reçoivent 500 pièces
    if (currentTotalProfiles < 31) {
      bonusCoins = 500;
    }
  } catch (err) {
    console.warn("Erreur vérification quotas:", err);
  }

  const initialBadges = isBeta ? ['beta_tester'] : [];

  const newProfile = { 
    ...profile, 
    userIds: userId ? [userId] : [], 
    friendCode: generateCode(), 
    createdAt: new Date().toISOString(),
    coins: bonusCoins,
    diamonds: 0,
    isBetaTester: isBeta,
    badges: initialBadges,
    inventory: [],
    equipped: {
      border: null,
      title: null,
      theme: null,
      mascot: null
    }
  };
  const docRef = await addDoc(collection(firestoreDb, "profiles"), newProfile);
  
  if (isBeta || bonusCoins > 0) {
    try {
      const msg = bonusCoins > 0 
        ? "Bienvenue pionnier ! Tu reçois le badge exclusif Beta Testeur 🎖️ ainsi qu'un cadeau de bienvenue de 500 pièces 🪙 !"
        : "Bienvenue pionnier ! Tu fais partie des premiers inscrits et reçois le badge exclusif Beta Testeur 🎖️ !";
      await addNotification({
        targetProfileId: docRef.id,
        message: msg,
        type: 'badge'
      });
    } catch (e) {
      console.warn("Erreur notification bêta:", e);
    }
  }

  return { ...newProfile, id: docRef.id };
};

const generateCode = () => {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
};

export const checkAndGenerateFriendCode = async (profileId) => {
  const profileRef = doc(firestoreDb, "profiles", profileId);
  const snap = await getDoc(profileRef);
  if (snap.exists()) {
    const data = snap.data();
    if (!data.friendCode) {
      const code = generateCode();
      await updateDoc(profileRef, { friendCode: code });
      return code;
    }
    return data.friendCode;
  }
  return null;
};

export const deleteProfile = async (id) => {
  await deleteDoc(doc(firestoreDb, "profiles", id));
};

export const setProfileOnlineStatus = async (profileId, isOnline) => {
  const profileRef = doc(firestoreDb, "profiles", profileId);
  try {
    await updateDoc(profileRef, { 
      online: isOnline,
      lastActive: new Date().toISOString()
    });
  } catch (error) {
    console.error("Erreur lors de la mise à jour du statut en ligne:", error);
  }
};

export const sendFriendRequest = async (senderId, receiverId) => {
  const senderRef = doc(firestoreDb, "profiles", senderId);
  const receiverRef = doc(firestoreDb, "profiles", receiverId);
  
  await updateDoc(senderRef, { sentRequests: arrayUnion(receiverId) });
  await updateDoc(receiverRef, { receivedRequests: arrayUnion(senderId) });

  const sender = await getProfile(senderId);
  if (sender) {
    await addNotification({
      targetProfileId: receiverId,
      title: "Nouvelle demande d'ami",
      message: `${sender.name} souhaite t'ajouter en ami.`,
      type: "social",
      link: "/social?tab=requests"
    });
  }
};

export const acceptFriendRequest = async (profileId, requesterId) => {
  const profileRef = doc(firestoreDb, "profiles", profileId);
  const requesterRef = doc(firestoreDb, "profiles", requesterId);
  
  await updateDoc(profileRef, { 
    receivedRequests: arrayRemove(requesterId),
    friends: arrayUnion(requesterId)
  });
  
  await updateDoc(requesterRef, { 
    sentRequests: arrayRemove(profileId),
    friends: arrayUnion(profileId)
  });

  const profile = await getProfile(profileId);
  if (profile) {
    await addNotification({
      targetProfileId: requesterId,
      title: "Demande acceptée",
      message: `${profile.name} a accepté ta demande d'ami.`,
      type: "social",
      link: "/social"
    });
  }
};

export const rejectFriendRequest = async (profileId, requesterId) => {
  const profileRef = doc(firestoreDb, "profiles", profileId);
  const requesterRef = doc(firestoreDb, "profiles", requesterId);
  
  await updateDoc(profileRef, { receivedRequests: arrayRemove(requesterId) });
  await updateDoc(requesterRef, { sentRequests: arrayRemove(profileId) });
};

export const cancelFriendRequest = async (senderId, receiverId) => {
  return rejectFriendRequest(receiverId, senderId);
};

export const removeFriend = async (profileId, friendId) => {
  const profileRef = doc(firestoreDb, "profiles", profileId);
  const friendRef = doc(firestoreDb, "profiles", friendId);
  
  await updateDoc(profileRef, { friends: arrayRemove(friendId) });
  await updateDoc(friendRef, { friends: arrayRemove(profileId) });
};

export const sendFriendRequestByCode = async (senderId, friendCode) => {
  if (!friendCode) throw new Error("Code ami invalide.");
  
  // Find user by friendCode
  const q = query(collection(firestoreDb, "profiles"), where("friendCode", "==", friendCode));
  const snap = await getDocs(q);
  if (snap.empty) {
    throw new Error("Aucun profil trouvé avec ce code.");
  }
  
  const receiverDoc = snap.docs[0];
  const receiverId = receiverDoc.id;
  
  if (receiverId === senderId) throw new Error("Tu ne peux pas t'ajouter toi-même !");
  
  const receiverData = receiverDoc.data();
  if (receiverData.friends?.includes(senderId)) throw new Error("Vous êtes déjà amis.");
  if (receiverData.receivedRequests?.includes(senderId)) throw new Error("Demande déjà envoyée.");
  
  const senderRef = doc(firestoreDb, "profiles", senderId);
  await updateDoc(senderRef, { sentRequests: arrayUnion(receiverId) });
  await updateDoc(receiverDoc.ref, { receivedRequests: arrayUnion(senderId) });
  
  const sender = await getProfile(senderId);
  if (sender) {
    await addNotification({
      targetProfileId: receiverId,
      title: "Nouvelle demande d'ami",
      message: `${sender.name} souhaite t'ajouter en ami.`,
      type: "social",
      link: "/social?tab=requests"
    });
  }
  
  return receiverData.name;
};

export const updateProfile = async (profile) => {
  const profileRef = doc(firestoreDb, "profiles", profile.id);
  const { id, ...data } = profile;
  await updateDoc(profileRef, data);
  return profile;
};

export const uploadProfilePicture = async (userId, file) => {
  const storageRef = ref(storage, `profiles/${userId}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

export const uploadGroupPicture = async (convoId, file) => {
  // Pour contourner le blocage Firebase Storage du forfait gratuit, on compresse
  // l'image et on la convertit en base64 pour la stocker directement dans Firestore.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_SIZE = 200; // Petite taille pour un avatar
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        // Qualité 0.7 pour alléger la chaîne base64 (bien en-dessous de la limite 1MB de Firestore)
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.onerror = (err) => reject(err);
      img.src = event.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

export const updateGroupAvatar = async (convoId, avatarUrl) => {
  const convoRef = doc(firestoreDb, "conversations", convoId);
  await updateDoc(convoRef, { avatarUrl });
};

export const uploadProgressPhoto = async (userId, file) => {
  const timestamp = Date.now();
  const storageRef = ref(storage, `progressPhotos/${userId}/${timestamp}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

export const uploadExerciseMedia = async (file) => {
  const timestamp = Date.now();
  // Safe path that might bypass strict user-only rules if any, or just use a general exercises folder
  const storageRef = ref(storage, `exercises/${timestamp}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

export const getProfile = async (id) => {
  if (!id) return null;
  const docSnap = await getDoc(doc(firestoreDb, "profiles", id));
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

export const listenToProfile = (profileId, callback) => {
  if (!profileId) return () => {};
  return onSnapshot(doc(firestoreDb, "profiles", profileId), (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
    } else {
      callback(null);
    }
  });
};

export const listenToFriends = (friendIds, callback) => {
  if (!friendIds || friendIds.length === 0) return () => {};
  
  const unsubscribes = [];
  let friendsMap = new Map();
  
  // Set up a listener for each friend
  friendIds.forEach(id => {
    const unsub = onSnapshot(doc(firestoreDb, "profiles", id), (docSnap) => {
      if (docSnap.exists()) {
        friendsMap.set(id, { id: docSnap.id, ...docSnap.data() });
        callback(Array.from(friendsMap.values()));
      }
    });
    unsubscribes.push(unsub);
  });
  
  return () => {
    unsubscribes.forEach(unsub => unsub());
  };
};

// -- PROFILES --
export const addXP = async (profileId, amount, source = 'general') => {
  if (!profileId) return;
  const profileRef = doc(firestoreDb, "profiles", profileId);
  const snap = await getDoc(profileRef);
  if (snap.exists()) {
    const data = snap.data();
    const currentXp = data.xp || 0;
    const today = new Date().toISOString().split('T')[0];
    
    let dailyXpStats = data.dailyXpStats || { date: today, weight: 0, body: 0 };
    if (dailyXpStats.date !== today) {
      dailyXpStats = { date: today, weight: 0, body: 0 };
    }

    if (source === 'weight') {
      if (dailyXpStats.weight >= 1) return; // Limite atteinte
      dailyXpStats.weight += 1;
    } else if (source === 'body') {
      if (dailyXpStats.body >= 1) return; // Limite atteinte
      dailyXpStats.body += 1;
    }
    
    await updateDoc(profileRef, { 
      xp: currentXp + amount,
      dailyXpStats
    });
  }
};

export const addBadgesToProfile = async (profileId, badgeIds = []) => {
  if (!profileId || !badgeIds || badgeIds.length === 0) return;
  try {
    const profileRef = doc(firestoreDb, "profiles", profileId);
    await updateDoc(profileRef, {
      badges: arrayUnion(...badgeIds)
    });
  } catch (e) {
    console.error("Error adding badges to profile:", e);
  }
};

export const addCurrency = async (profileId, coinsToAdd = 0, diamondsToAdd = 0) => {
  if (!profileId) return;
  const profileRef = doc(firestoreDb, "profiles", profileId);
  const snap = await getDoc(profileRef);
  if (snap.exists()) {
    const data = snap.data();
    const currentCoins = data.coins || 0;
    const currentDiamonds = data.diamonds || 0;
    
    await updateDoc(profileRef, {
      coins: currentCoins + coinsToAdd,
      diamonds: currentDiamonds + diamondsToAdd
    });
  }
};

export const purchaseItem = async (profileId, item) => {
  if (!profileId || !item) throw new Error("Paramètres invalides");
  const profileRef = doc(firestoreDb, "profiles", profileId);
  const snap = await getDoc(profileRef);
  
  if (snap.exists()) {
    const data = snap.data();
    const coins = data.coins || 0;
    const diamonds = data.diamonds || 0;
    
    // Check price
    if (item.currency === 'coins' && coins < item.price) throw new Error("Fonds insuffisants (Pièces)");
    if (item.currency === 'diamonds' && diamonds < item.price) throw new Error("Fonds insuffisants (Diamants)");
    
    // Deduct
    const updates = {};
    if (item.currency === 'coins') updates.coins = coins - item.price;
    if (item.currency === 'diamonds') updates.diamonds = diamonds - item.price;
    
    // Add to inventory
    const inventory = data.inventory || [];
    if (inventory.includes(item.id)) throw new Error("Tu possèdes déjà cet objet");
    
    updates.inventory = [...inventory, item.id];
    
    await updateDoc(profileRef, updates);
    return true;
  }
  return false;
};

export const equipItem = async (profileId, itemCategory, itemId) => {
  const profileRef = doc(firestoreDb, "profiles", profileId);
  const snap = await getDoc(profileRef);
  
  if (snap.exists()) {
    const data = snap.data();
    const equipped = data.equipped || {};
    
    // Si l'objet est déjà équipé, on le déséquipe (toggle)
    if (equipped[itemCategory] === itemId) {
       equipped[itemCategory] = null;
    } else {
       equipped[itemCategory] = itemId;
    }
    
    await updateDoc(profileRef, { equipped });
  }
};

// -- EXERCISES --
export const getExercises = async (profileId) => {
  const ADMIN_ID = 'VX3fSmgFvr20XLYuzZv8';

  const [globalSnap, personalSnap] = await Promise.all([
    getDocs(query(collection(firestoreDb, "exercises"), where("isGlobal", "==", true))),
    profileId && profileId !== ADMIN_ID 
      ? getDocs(query(collection(firestoreDb, "exercises"), where("profileId", "==", profileId))) 
      : { forEach: () => {} }
  ]);

  const exercisesMap = {};

  globalSnap.forEach(doc => {
    exercisesMap[doc.id] = { ...doc.data(), id: doc.id };
  });

  if (profileId !== ADMIN_ID) {
    personalSnap.forEach(doc => {
      const data = doc.data();
      if (data.baseId) {
        if (data.hidden) {
          delete exercisesMap[data.baseId];
        } else {
          exercisesMap[data.baseId] = { ...data, id: doc.id };
        }
      } else {
        exercisesMap[doc.id] = { ...data, id: doc.id };
      }
    });
  }

  return Object.values(exercisesMap);
};

export const addExercise = async (exercise, profileId) => {
  const ADMIN_ID = 'VX3fSmgFvr20XLYuzZv8';
  const isGlobal = profileId === ADMIN_ID;
  const newEx = { ...exercise, profileId: isGlobal ? null : profileId, isGlobal };
  const docRef = await addDoc(collection(firestoreDb, "exercises"), newEx);
  return { ...newEx, id: docRef.id };
};

// -- WORKOUTS --
export const getWorkouts = async (profileId) => {
  if (!profileId) return [];
  try {
    const q = query(
      collection(firestoreDb, "workouts"), 
      where("profileId", "==", profileId)
    );
    const querySnapshot = await getDocs(q);
    const workouts = [];
    querySnapshot.forEach((doc) => {
      workouts.push({ ...doc.data(), id: doc.id });
    });
    return workouts.sort((a, b) => new Date(b.date) - new Date(a.date));
  } catch(e) {
    console.error("Error fetching workouts:", e);
    return [];
  }
};

export const cleanUndefined = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  Object.keys(obj).forEach(key => {
    if (obj[key] === undefined) {
      delete obj[key];
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      cleanUndefined(obj[key]);
    }
  });
  return obj;
};

export const saveWorkout = async (workout) => {

  if (workout.id) {
    const workoutRef = doc(firestoreDb, "workouts", workout.id);
    const { id, ...data } = workout;
    
    // Clean up undefined fields recursively
    const cleanedData = cleanUndefined(data);

    await setDoc(workoutRef, cleanedData, { merge: true });
    return workout;
  } else {
    const docRef = await addDoc(collection(firestoreDb, "workouts"), workout);
    return { ...workout, id: docRef.id };
  }
};

export const deleteWorkout = async (id) => {
  await deleteDoc(doc(firestoreDb, "workouts", id));
};

// -- WEIGHTS --
export const getWeights = async (profileId) => {
  if (!profileId) return [];
  try {
    const q = query(
      collection(firestoreDb, "weights"), 
      where("profileId", "==", profileId)
    );
    const querySnapshot = await getDocs(q);
    const weights = [];
    querySnapshot.forEach((doc) => {
      weights.push({ ...doc.data(), id: doc.id });
    });
    return weights.sort((a, b) => new Date(a.date) - new Date(b.date));
  } catch(e) {
    console.error("Error fetching weights:", e);
    return [];
  }
};

export const addWeight = async (weight) => {
  const docRef = await addDoc(collection(firestoreDb, "weights"), weight);
  return { ...weight, id: docRef.id };
};

export const deleteWeight = async (id) => {
  await deleteDoc(doc(firestoreDb, "weights", id));
};

export const updateExercise = async (exercise, profileId) => {
  const ADMIN_ID = 'VX3fSmgFvr20XLYuzZv8';
  const { id, ...data } = exercise;

  if (profileId === ADMIN_ID && exercise.isGlobal) {
    await updateDoc(doc(firestoreDb, "exercises", id), data);
    return exercise;
  }

  if (exercise.isGlobal) {
    const newData = { ...data, baseId: id, profileId, isGlobal: false };
    const docRef = await addDoc(collection(firestoreDb, "exercises"), newData);
    return { ...newData, id: docRef.id };
  } else {
    await updateDoc(doc(firestoreDb, "exercises", id), data);
    return exercise;
  }
};

export const deleteExercise = async (exercise, profileId) => {
  const ADMIN_ID = 'VX3fSmgFvr20XLYuzZv8';
  if (profileId === ADMIN_ID && exercise.isGlobal) {
    await deleteDoc(doc(firestoreDb, "exercises", exercise.id));
    return;
  }
  
  if (exercise.isGlobal) {
    await addDoc(collection(firestoreDb, "exercises"), {
      baseId: exercise.id,
      profileId: profileId,
      hidden: true,
      isGlobal: false
    });
  } else if (exercise.baseId) {
    await updateDoc(doc(firestoreDb, "exercises", exercise.id), { hidden: true });
  } else {
    await deleteDoc(doc(firestoreDb, "exercises", exercise.id));
  }
};

// -- GOALS --
export const getGoals = async (profileId) => {
  if (!profileId) return [];
  try {
    const q = query(
      collection(firestoreDb, "goals"), 
      where("profileId", "==", profileId)
    );
    const querySnapshot = await getDocs(q);
    const goals = [];
    querySnapshot.forEach((doc) => {
      goals.push({ ...doc.data(), id: doc.id });
    });
    return goals;
  } catch(e) {
    console.error("Error fetching goals:", e);
    return [];
  }
};

export const addGoal = async (goal) => {
  const docRef = await addDoc(collection(firestoreDb, "goals"), goal);
  return { ...goal, id: docRef.id };
};

export const updateGoal = async (goal) => {
  const goalRef = doc(firestoreDb, "goals", goal.id);
  const { id, ...data } = goal;
  await updateDoc(goalRef, data);
  return goal;
};

export const deleteGoal = async (id) => {
  await deleteDoc(doc(firestoreDb, "goals", id));
};

// -- ROUTINES --
export const getRoutines = async (profileId) => {
  if (!profileId) return [];
  try {
    const q = query(
      collection(firestoreDb, "routines"), 
      where("profileId", "==", profileId)
    );
    const querySnapshot = await getDocs(q);
    const routines = [];
    querySnapshot.forEach((doc) => {
      routines.push({ ...doc.data(), id: doc.id });
    });
    return routines;
  } catch(e) {
    console.error("Error fetching routines:", e);
    return [];
  }
};

export const addRoutine = async (routine) => {
  const docRef = await addDoc(collection(firestoreDb, "routines"), routine);
  return { ...routine, id: docRef.id };
};

export const updateRoutine = async (routineId, data) => {
  const routineRef = doc(firestoreDb, "routines", routineId);
  await updateDoc(routineRef, data);
};

export const deleteRoutine = async (id) => {
  await deleteDoc(doc(firestoreDb, "routines", id));
};

// -- POSTS (FORUM) --
export const listenToPosts = (callback) => {
  const q = query(collection(firestoreDb, "posts"), orderBy("timestamp", "desc"));
  return onSnapshot(q, (snapshot) => {
    const posts = [];
    snapshot.forEach((doc) => {
      posts.push({ ...doc.data(), id: doc.id });
    });
    callback(posts);
  });
};

export const addPost = async (post) => {
  const docRef = await addDoc(collection(firestoreDb, "posts"), {
    ...post,
    timestamp: Date.now(),
    likes: [],
    comments: []
  });
  return { ...post, id: docRef.id };
};

export const deletePost = async (id) => {
  await deleteDoc(doc(firestoreDb, "posts", id));
};

export const toggleLikePost = async (postId, profileId, currentLikes) => {
  const postRef = doc(firestoreDb, "posts", postId);
  let newLikes = [...(currentLikes || [])];
  if (newLikes.includes(profileId)) {
    newLikes = newLikes.filter(id => id !== profileId);
  } else {
    newLikes.push(profileId);
  }
  await updateDoc(postRef, { likes: newLikes });
};

export const addCommentToPost = async (postId, currentComments, comment) => {
  const postRef = doc(firestoreDb, "posts", postId);
  const commentId = Math.random().toString(36).substring(2);
  const newComments = [...(currentComments || []), { ...comment, timestamp: Date.now(), id: commentId, likes: [] }];
  await updateDoc(postRef, { comments: newComments });
  return commentId;
};

export const toggleLikeComment = async (postId, currentComments, commentId, profileId) => {
  const postRef = doc(firestoreDb, "posts", postId);
  const newComments = [...(currentComments || [])];
  const commentIndex = newComments.findIndex(c => c.id === commentId);
  
  if (commentIndex !== -1) {
    const comment = { ...newComments[commentIndex] };
    const likes = [...(comment.likes || [])];
    
    if (likes.includes(profileId)) {
      comment.likes = likes.filter(id => id !== profileId);
    } else {
      likes.push(profileId);
      comment.likes = likes;
    }
    
    newComments[commentIndex] = comment;
    await updateDoc(postRef, { comments: newComments });
  }
};

export const uploadPostMedia = async (file) => {
  const timestamp = Date.now();
  const storageRef = ref(storage, `posts/${timestamp}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};
// -- NOTIFICATIONS --
export const listenToNotifications = (profileId, callback) => {
  const q = query(collection(firestoreDb, "notifications"), where("targetProfileId", "==", profileId));
  return onSnapshot(q, (snapshot) => {
    const notifs = [];
    snapshot.forEach((doc) => {
      notifs.push({ ...doc.data(), id: doc.id });
    });
    notifs.sort((a, b) => b.timestamp - a.timestamp);
    callback(notifs);
  });
};

export const addNotification = async (notification) => {
  const docRef = await addDoc(collection(firestoreDb, "notifications"), {
    ...notification,
    timestamp: Date.now(),
    read: false
  });
  return { ...notification, id: docRef.id };
};

export const markNotificationRead = async (id) => {
  const notifRef = doc(firestoreDb, "notifications", id);
  await updateDoc(notifRef, { read: true });
};

export const markAllNotificationsRead = async (profileId) => {
  const q = query(
    collection(firestoreDb, "notifications"), 
    where("targetProfileId", "==", profileId)
  );
  const snap = await getDocs(q);
  const promises = snap.docs
    .filter(d => !d.data().read)
    .map(d => updateDoc(doc(firestoreDb, "notifications", d.id), { read: true }));
  await Promise.all(promises);
};

export const deleteNotification = async (id) => {
  await deleteDoc(doc(firestoreDb, "notifications", id));
};

export const deleteAllNotifications = async (profileId) => {
  const q = query(
    collection(firestoreDb, "notifications"), 
    where("targetProfileId", "==", profileId)
  );
  const snap = await getDocs(q);
  const promises = snap.docs.map(d => deleteDoc(doc(firestoreDb, "notifications", d.id)));
  await Promise.all(promises);
};

export const notifyAllOtherProfiles = async (currentProfileId, message, link) => {
  const profiles = await getProfiles();
  const promises = profiles
    .filter(p => p.id !== currentProfileId)
    .map(p => addNotification({
      targetProfileId: p.id,
      message,
      link: link || '/forum'
    }));
  await Promise.all(promises);
};

export const getProfileByName = async (name) => {
  const q = query(collection(firestoreDb, "profiles"), where("name", "==", name));
  const snap = await getDocs(q);
  if (!snap.empty) {
    return { id: snap.docs[0].id, ...snap.docs[0].data() };
  }
  return null;
};

// --- FEED (SOCIAL) ---
export const getFriendsFeed = async (friendsIds) => {
  if (!friendsIds || friendsIds.length === 0) return [];
  
  // We want the last 30 days of workouts from friends
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  

  // Firebase 'in' queries are limited to 10 items.
  // We'll chunk the friends array if needed, or just fetch all and filter in memory if chunks > 10.
  // For simplicity here, we'll chunk by 10.
  let allEvents = [];
  
  const chunks = [];
  for (let i = 0; i < friendsIds.length; i += 10) {
    chunks.push(friendsIds.slice(i, i + 10));
  }
  
  for (const chunk of chunks) {
    // 1. Fetch Workouts
    const wq = query(
      collection(firestoreDb, "workouts"),
      where("profileId", "in", chunk)
    );
    const wSnap = await getDocs(wq);
    wSnap.forEach(doc => {
      const data = doc.data();
      if (new Date(data.date) >= thirtyDaysAgo) {
        allEvents.push({ type: 'workout', feedDate: data.timestamp || new Date(data.date).getTime(), ...data, id: doc.id });
      }
    });

    // 2. Fetch Posts
    const pq = query(
      collection(firestoreDb, "posts"),
      where("profileId", "in", chunk)
    );
    const pSnap = await getDocs(pq);
    pSnap.forEach(doc => {
      const data = doc.data();
      if (data.timestamp >= thirtyDaysAgo.getTime()) {
        allEvents.push({ type: 'post', feedDate: data.timestamp, ...data, id: doc.id });
      }
    });
  }
  
  // Sort all combined events by date desc
  allEvents.sort((a, b) => b.feedDate - a.feedDate);
  return allEvents;
};

// -- WEEKLY ANALYSIS --
export const getLatestWeeklyAnalysis = async (profileId) => {
  if (!profileId) return null;
  const q = query(
    collection(firestoreDb, "weekly_analysis"),
    where("profileId", "==", profileId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  // We should have at most one document per profile representing the latest analysis
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const saveWeeklyAnalysis = async (profileId, analysisData) => {
  const cleanUndefined = (obj) => {
    Object.keys(obj).forEach(key => {
      if (obj[key] === undefined) delete obj[key];
      else if (typeof obj[key] === 'object' && obj[key] !== null) cleanUndefined(obj[key]);
    });
    return obj;
  };

  const currentAnalysis = await getLatestWeeklyAnalysis(profileId);
  const dataToSave = cleanUndefined({ profileId, ...analysisData, timestamp: Date.now() });

  if (currentAnalysis) {
    const docRef = doc(firestoreDb, "weekly_analysis", currentAnalysis.id);
    await updateDoc(docRef, dataToSave);
  } else {
    await addDoc(collection(firestoreDb, "weekly_analysis"), dataToSave);
  }
};

export const exportProfileData = async (profileId) => {
  if (!profileId) return null;
  const profile = await getProfile(profileId);
  const workouts = await getWorkouts(profileId);
  const routines = await getRoutines(profileId);
  const weights = await getWeights(profileId);
  const goals = await getGoals(profileId);
  const weeklyAnalysis = await getLatestWeeklyAnalysis(profileId);
  
  return {
    exportDate: new Date().toISOString(),
    profile,
    workouts,
    routines,
    weights,
    goals,
    weeklyAnalysis
  };
};

// -- FEEDBACK (SUGGESTIONS & BUGS) --
export const addFeedback = async (feedback) => {
  const docRef = await addDoc(collection(firestoreDb, "feedbacks"), {
    ...feedback,
    timestamp: Date.now()
  });
  return { ...feedback, id: docRef.id };
};

export const getFeedbacks = async () => {
  try {
    const q = query(
      collection(firestoreDb, "feedbacks"),
      orderBy("timestamp", "desc")
    );
    const snap = await getDocs(q);
    const feedbacks = [];
    snap.forEach((doc) => {
      feedbacks.push({ ...doc.data(), id: doc.id });
    });
    return feedbacks;
  } catch(e) {
    console.error("Error fetching feedbacks:", e);
    return [];
  }
};

export const updateFeedback = async (id, data) => {
  const docRef = doc(firestoreDb, "feedbacks", id);
  await updateDoc(docRef, data);
};

export const deleteFeedback = async (id) => {
  await deleteDoc(doc(firestoreDb, "feedbacks", id));
};

// -- MESSAGING --
export const getConversationId = (user1, user2) => {
  return user1 < user2 ? `${user1}_${user2}` : `${user2}_${user1}`;
};

export const getOrCreateConversation = async (user1, user2) => {
  const convoId = getConversationId(user1, user2);
  const convoRef = doc(firestoreDb, "conversations", convoId);
  const snap = await getDoc(convoRef);
  
  if (!snap.exists()) {
    await setDoc(convoRef, {
      participants: [user1, user2],
      updatedAt: Date.now(),
      lastMessage: '',
      unreadCount: { [user1]: 0, [user2]: 0 }
    });
  }
  return convoId;
};

export const createGroupConversation = async (name, creatorId, participantIds) => {
  const allParticipants = [...new Set([creatorId, ...participantIds])];
  const docRef = await addDoc(collection(firestoreDb, "conversations"), {
    isGroup: true,
    name,
    adminId: creatorId,
    participants: allParticipants,
    updatedAt: Date.now(),
    lastMessage: 'Groupe créé',
    unreadCount: allParticipants.reduce((acc, p) => ({ ...acc, [p]: 0 }), {})
  });
  return docRef.id;
};

export const updateGroupName = async (convoId, newName) => {
  const convoRef = doc(firestoreDb, "conversations", convoId);
  await updateDoc(convoRef, { name: newName });
};

export const addMembersToGroup = async (convoId, newParticipantIds) => {
  const convoRef = doc(firestoreDb, "conversations", convoId);
  const convoSnap = await getDoc(convoRef);
  if (convoSnap.exists()) {
    const data = convoSnap.data();
    const updatedParticipants = [...new Set([...data.participants, ...newParticipantIds])];
    const newUnreadCount = { ...data.unreadCount };
    newParticipantIds.forEach(p => {
      if (newUnreadCount[p] === undefined) newUnreadCount[p] = 0;
    });
    
    await updateDoc(convoRef, {
      participants: updatedParticipants,
      unreadCount: newUnreadCount
    });
  }
};

export const leaveGroup = async (convoId, userId, userName = "Un membre") => {
  const convoRef = doc(firestoreDb, "conversations", convoId);
  const convoSnap = await getDoc(convoRef);
  if (convoSnap.exists()) {
    const data = convoSnap.data();
    const updatedParticipants = data.participants.filter(p => p !== userId);
    
    if (updatedParticipants.length === 0) {
      await deleteDoc(convoRef);
    } else {
      let newAdmin = data.adminId;
      if (data.adminId === userId) {
        newAdmin = updatedParticipants[0];
      }
      await updateDoc(convoRef, {
        participants: updatedParticipants,
        adminId: newAdmin
      });
      
      // Send system message to notify others
      await sendMessage(convoId, 'system', null, `${userName} a quitté le groupe`, 'system');
    }
  }
};

export const listenToConversations = (profileId, callback) => {
  if (!profileId) return () => {};
  const q = query(
    collection(firestoreDb, "conversations"),
    where("participants", "array-contains", profileId)
  );
  return onSnapshot(q, (snapshot) => {
    const convos = [];
    snapshot.forEach(doc => {
      convos.push({ id: doc.id, ...doc.data() });
    });
    convos.sort((a, b) => b.updatedAt - a.updatedAt);
    callback(convos);
  });
};

export const listenToMessages = (convoId, limitCount, callback) => {
  if (!convoId) return () => {};
  const q = query(
    collection(firestoreDb, `conversations/${convoId}/messages`),
    orderBy("timestamp", "desc"),
    limit(limitCount)
  );
  return onSnapshot(q, (snapshot) => {
    const msgs = [];
    snapshot.forEach(doc => {
      msgs.push({ id: doc.id, ...doc.data() });
    });
    // Reverse because we queried desc (newest first) but we want oldest first in the UI
    callback(msgs.reverse());
  });
};

export const sendMessage = async (convoId, senderId, receiverId, text, type = 'text', replyTo = null) => {
  const msgData = {
    senderId,
    text,
    type,
    timestamp: Date.now()
  };
  if (replyTo) msgData.replyTo = replyTo;
  
  await addDoc(collection(firestoreDb, `conversations/${convoId}/messages`), msgData);
  
  const convoRef = doc(firestoreDb, "conversations", convoId);
  const convoSnap = await getDoc(convoRef);
  
  if (convoSnap.exists()) {
    const data = convoSnap.data();
    const currentUnreadCount = data.unreadCount || {};
    
    const newUnreadCount = { ...currentUnreadCount };
    const participantsToNotify = [];
    
    if (data.participants) {
      data.participants.forEach(p => {
        if (p !== senderId) {
          participantsToNotify.push(p);
          newUnreadCount[p] = (newUnreadCount[p] || 0) + 1;
        }
      });
    } else if (receiverId) {
      // Fallback for old conversations
      participantsToNotify.push(receiverId);
      newUnreadCount[receiverId] = (currentUnreadCount[receiverId] || 0) + 1;
    }
    
    let lastMessageText = text;
    if (type === 'gif') lastMessageText = 'Un GIF a été envoyé';
    if (type === 'photo') lastMessageText = 'Une photo a été envoyée';
    
    await updateDoc(convoRef, {
      lastMessage: lastMessageText,
      updatedAt: Date.now(),
      unreadCount: newUnreadCount
    });
    
    const senderProfile = await getProfile(senderId);
    
    for (const p of participantsToNotify) {
      const q = query(
        collection(firestoreDb, "notifications"),
        where("targetProfileId", "==", p),
        where("convoId", "==", convoId),
        where("read", "==", false)
      );
      const snap = await getDocs(q);
      
      const groupName = data.name || "le groupe";
      const truncatedGroupName = groupName.length > 15 ? groupName.substring(0, 15) + '...' : groupName;

      const notifData = {
        targetProfileId: p,
        title: data.isGroup ? `Nouveau message dans ${truncatedGroupName}` : "Nouveau message",
        message: data.isGroup ? `${senderProfile?.name || "Quelqu'un"} a écrit dans "${truncatedGroupName}".` : `${senderProfile?.name || "Quelqu'un"} a envoyé un message.`,
        type: "social",
        link: data.isGroup ? `/messages?convo=${convoId}` : `/messages?user=${senderId}`,
        convoId: convoId,
        timestamp: Date.now(),
        read: false
      };
      
      if (!snap.empty) {
        const notifRef = snap.docs[0].ref;
        await updateDoc(notifRef, notifData);
      } else {
        await addDoc(collection(firestoreDb, "notifications"), notifData);
      }
    }
  }
};

export const markConversationAsRead = async (convoId, profileId) => {
  const convoRef = doc(firestoreDb, "conversations", convoId);
  const convoSnap = await getDoc(convoRef);
  
  if (convoSnap.exists()) {
    const data = convoSnap.data();
    const currentUnreadCount = data.unreadCount || {};
    if (currentUnreadCount[profileId] > 0) {
      const newUnreadCount = {
        ...currentUnreadCount,
        [profileId]: 0
      };
      await updateDoc(convoRef, { unreadCount: newUnreadCount });
    }
  }

  try {
    const q = query(
      collection(firestoreDb, "notifications"),
      where("targetProfileId", "==", profileId),
      where("read", "==", false),
      where("convoId", "==", convoId)
    );
    const notifSnap = await getDocs(q);
    const promises = notifSnap.docs.map(d => updateDoc(d.ref, { read: true }));
    await Promise.all(promises);
  } catch (e) {
    console.error("Error marking notifications as read:", e);
  }
};

export const setTypingStatus = async (convoId, profileId, isTyping) => {
  if (!convoId || !profileId) return;
  const convoRef = doc(firestoreDb, "conversations", convoId);
  try {
    await updateDoc(convoRef, {
      [`typing.${profileId}`]: isTyping
    });
  } catch (e) {
    console.error("Error updating typing status:", e);
  }
};

export const uploadMessageMedia = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_SIZE = 800; // Good enough for chat images
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7)); // Compress to jpeg
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = event.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

export const toggleMessageReaction = async (convoId, messageId, profileId, emoji) => {
  const msgRef = doc(firestoreDb, `conversations/${convoId}/messages`, messageId);
  const snap = await getDoc(msgRef);
  if (snap.exists()) {
    const data = snap.data();
    const reactions = data.reactions || {};
    const previousReaction = reactions[profileId];
    
    if (reactions[profileId] === emoji) {
      delete reactions[profileId];
    } else {
      reactions[profileId] = emoji;
    }
    
    await updateDoc(msgRef, { reactions });
  }
};

export const db = {
  getProfiles, addProfile, updateProfile, deleteProfile, setProfileOnlineStatus, uploadProfilePicture, uploadProgressPhoto, uploadExerciseMedia, addXP, addCurrency, addBadgesToProfile, purchaseItem, equipItem, getProfile, listenToProfile, listenToFriends,
  sendFriendRequest, acceptFriendRequest, rejectFriendRequest, cancelFriendRequest, removeFriend, checkAndGenerateFriendCode, sendFriendRequestByCode, getFriendsFeed,
  getExercises, addExercise, updateExercise, deleteExercise,
  getWorkouts, saveWorkout, deleteWorkout,
  getWeights, addWeight, deleteWeight,
  getGoals, addGoal, updateGoal, deleteGoal,
  getRoutines, addRoutine, updateRoutine, deleteRoutine,
  listenToPosts, addPost, deletePost, toggleLikePost, addCommentToPost, toggleLikeComment, uploadPostMedia,
  listenToNotifications, addNotification, markNotificationRead, markAllNotificationsRead, deleteNotification, deleteAllNotifications, notifyAllOtherProfiles, getProfileByName,
  getLatestWeeklyAnalysis, saveWeeklyAnalysis, exportProfileData,
  addFeedback, getFeedbacks, updateFeedback, deleteFeedback,
  getConversationId, getOrCreateConversation, createGroupConversation, updateGroupName, addMembersToGroup, leaveGroup, listenToConversations, listenToMessages, sendMessage, markConversationAsRead, setTypingStatus, uploadMessageMedia, toggleMessageReaction, uploadGroupPicture, updateGroupAvatar,
  subscribeToActiveBoss, subscribeToTopHunters, subscribeToUserParticipation, recordWorkoutBossDamage, recordTapDamage, queueTapDamage, claimBossReward, resetBossHp, calculateWorkoutTonnage
};

export { 
  subscribeToActiveBoss, 
  subscribeToTopHunters, 
  subscribeToUserParticipation, 
  recordWorkoutBossDamage, 
  recordTapDamage, 
  queueTapDamage,
  claimBossReward, 
  resetBossHp, 
  calculateWorkoutTonnage 
};



